import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { Box } from '@mantine/core';
import { createStyles } from '@mantine/styles';

const useStyles = createStyles(() => ({
  container: {
    borderRadius: '8px',
    padding: '16px'
  },
  barContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: '100%',
    justifyContent: 'center'
  },
  bar: {
    width: 6,
    backgroundColor: '#000 !important',
    borderRadius: '999px',
    transition: 'height 100ms ease',
    minHeight: 8
  }
}));

// Configuration for audio visualization animation
const ANIMATION_CONFIG = {
  BAR_COUNT: 8,
  MIN_HEIGHT: 8,
  MAX_HEIGHT: 40,
  THRESHOLD: 0.2,
  MAX_EFFECTIVE_VOLUME: 0.7,
  WAVE_SPEEDS: {
    SLOW: 0.003,
    MEDIUM: 0.006,
    FAST: 0.009
  },
  WAVE_PHASES: {
    PRIMARY: 0.2,
    SECONDARY: 0.4,
    TERTIARY: 0.6
  },
  SMOOTHING: {
    RISE: 0.3,
    FALL: 0.1
  }
} as const;

interface AudioVisualizerProps {
  // Volume input between 0 and 1
  volume: number;

  // Whether the visualizer is active
  active: boolean;
}

interface BarProps {
  height: number;
}

// Individual bar component for the visualizer
const Bar = memo(function Bar({ height }: BarProps) {
  const { classes } = useStyles();
  return <Box className={classes.bar} style={{ height: `${height}px` }} />;
});

// Audio visualizer component that renders animated bars based on volume input
export const AudioVisualizer = memo(function AudioVisualizer({ volume = 0, active = false }: AudioVisualizerProps) {
  const { classes } = useStyles();
  const barsRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<boolean>(active);

  // Animation refs
  const animationFrameRef = useRef<number | undefined>(undefined);
  const timeRef = useRef<number>(Date.now());
  const lastVolumeRef = useRef<number>(volume);
  const smoothedVolumeRef = useRef<number>(volume);
  const heightsRef = useRef<number[]>(Array(ANIMATION_CONFIG.BAR_COUNT).fill(ANIMATION_CONFIG.MIN_HEIGHT));
  const phaseOffsetsRef = useRef<number[]>(
    Array(ANIMATION_CONFIG.BAR_COUNT)
      .fill(0)
      .map(() => Math.random() * Math.PI * 2)
  );

  // Resets all bars to minimum height and clears volume state
  const resetBars = useCallback(() => {
    if (!barsRef.current) return;

    const bars = barsRef.current.children;
    for (let i = 0; i < bars.length; i++) {
      (bars[i] as HTMLElement).style.height = `${ANIMATION_CONFIG.MIN_HEIGHT}px`;
    }

    smoothedVolumeRef.current = 0;
    lastVolumeRef.current = 0;
    heightsRef.current = Array(ANIMATION_CONFIG.BAR_COUNT).fill(ANIMATION_CONFIG.MIN_HEIGHT);
  }, []);

  // Handle reset when transitioning from active to inactive
  useEffect(() => {
    if (activeRef.current && !active) {
      resetBars();
    }
    activeRef.current = active;
  }, [active, resetBars]);

  // Updates the smoothed volume based on current volume and rise/fall rates
  const updateSmoothedVolume = useCallback((currentVolume: number) => {
    const riseOrFallFactor =
      currentVolume > lastVolumeRef.current ? ANIMATION_CONFIG.SMOOTHING.RISE : ANIMATION_CONFIG.SMOOTHING.FALL;

    smoothedVolumeRef.current += (currentVolume - smoothedVolumeRef.current) * riseOrFallFactor;

    lastVolumeRef.current = smoothedVolumeRef.current;
  }, []);

  // Calculates wave animations for a bar at the given index
  const calculateWaveEffect = useCallback((index: number, timestamp: number, centeredness: number) => {
    const phaseOffset = phaseOffsetsRef.current[index];

    // Primary slow wave
    const wave1 =
      Math.sin(
        timestamp * ANIMATION_CONFIG.WAVE_SPEEDS.SLOW + index * ANIMATION_CONFIG.WAVE_PHASES.PRIMARY + phaseOffset
      ) * 0.5;

    // Secondary medium-speed wave with centeredness factor
    const wave2 =
      Math.sin(
        timestamp * ANIMATION_CONFIG.WAVE_SPEEDS.MEDIUM +
          index * ANIMATION_CONFIG.WAVE_PHASES.SECONDARY +
          phaseOffset * 1.5
      ) *
      0.3 *
      centeredness;

    // Tertiary fast wave
    const wave3 =
      Math.sin(
        timestamp * ANIMATION_CONFIG.WAVE_SPEEDS.FAST +
          index * ANIMATION_CONFIG.WAVE_PHASES.TERTIARY +
          phaseOffset * 0.7
      ) * 0.2;

    // Combine all waves and normalize to 0-1 range
    return (wave1 + wave2 + wave3 + 1) / 2;
  }, []);

  // Updates the height of each bar based on volume and animation state
  const updateHeights = useCallback(() => {
    if (!active || !barsRef.current) {
      resetBars();
      return;
    }

    // If volume is 0, cleanup and exit
    if (volume === 0) {
      smoothedVolumeRef.current = 0;
      lastVolumeRef.current = 0;
      heightsRef.current = Array(ANIMATION_CONFIG.BAR_COUNT).fill(ANIMATION_CONFIG.MIN_HEIGHT);
      return;
    }

    const now = Date.now();
    timeRef.current = now;

    // Below threshold but not 0, just set minimum height
    if (volume <= ANIMATION_CONFIG.THRESHOLD) {
      heightsRef.current = Array(ANIMATION_CONFIG.BAR_COUNT).fill(ANIMATION_CONFIG.MIN_HEIGHT);
    } else {
      // Normal animation code for above threshold
      updateSmoothedVolume(volume);

      // Reset phase offsets if crossing threshold from below
      if (volume > ANIMATION_CONFIG.THRESHOLD && lastVolumeRef.current <= ANIMATION_CONFIG.THRESHOLD) {
        phaseOffsetsRef.current = phaseOffsetsRef.current.map(() => Math.random() * Math.PI * 2);
      }

      // Calculate effective volume as normalized value between threshold and max
      const effectiveVolume = Math.min(
        1,
        (volume - ANIMATION_CONFIG.THRESHOLD) / (ANIMATION_CONFIG.MAX_EFFECTIVE_VOLUME - ANIMATION_CONFIG.THRESHOLD)
      );

      // Calculate new heights for each bar
      heightsRef.current = heightsRef.current.map((_, index) => {
        // How close the bar is to the center (0-1)
        const centeredness = 1 - Math.abs(index - ANIMATION_CONFIG.BAR_COUNT / 2) / (ANIMATION_CONFIG.BAR_COUNT / 2);

        // Get combined wave effect
        const combinedWave = calculateWaveEffect(index, now, centeredness);

        // Calculate base height
        const baseHeight =
          ANIMATION_CONFIG.MIN_HEIGHT +
          combinedWave * effectiveVolume * (ANIMATION_CONFIG.MAX_HEIGHT - ANIMATION_CONFIG.MIN_HEIGHT);

        // Add randomness based on volume changes
        const randomFactor = Math.abs(volume - lastVolumeRef.current) * 5;
        const noise = (Math.random() - 0.5) * effectiveVolume * (3 + randomFactor) * (centeredness + 0.5);

        // Clamp final height between min and max
        return Math.max(ANIMATION_CONFIG.MIN_HEIGHT, Math.min(ANIMATION_CONFIG.MAX_HEIGHT, baseHeight + noise));
      });
    }

    // Update DOM directly for performance
    const bars = barsRef.current.children;
    for (let i = 0; i < bars.length; i++) {
      (bars[i] as HTMLElement).style.height = `${heightsRef.current[i]}px`;
    }

    // Request next frame
    animationFrameRef.current = requestAnimationFrame(updateHeights);
  }, [volume, active, resetBars, updateSmoothedVolume, calculateWaveEffect]);

  // Main animation effect - handles setup, running, and cleanup of animation
  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }

    // Initialize state based on current props
    if (volume === 0 || !active) {
      resetBars();
    } else {
      // Start animation
      timeRef.current = Date.now();
      animationFrameRef.current = requestAnimationFrame(updateHeights);
    }

    // Cleanup on unmount or props change
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, [volume, active, updateHeights, resetBars]);

  /**
   * Create bar components - note we're using an empty dependency array
   * because we're updating heights via DOM manipulation in updateHeights
   */
  const bars = useMemo(
    () =>
      Array(ANIMATION_CONFIG.BAR_COUNT)
        .fill(0)
        .map((_, index) => <Bar key={index} height={heightsRef.current[index]} />),
    [] // Empty dependency array since we're updating heights via DOM manipulation
  );

  return (
    <Box className={classes.container}>
      <Box ref={barsRef} className={classes.barContainer}>
        {bars}
      </Box>
    </Box>
  );
});
